import React, { useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useMutation } from "@apollo/client/react";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { usePopupState, bindMenu, bindTrigger } from "material-ui-popup-state/hooks";
import MuiMenu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IosShareIcon from "@mui/icons-material/IosShare";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import HistoryIcon from "@mui/icons-material/History";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ChecklistIcon from "@mui/icons-material/Checklist";
import Tooltip from "@mui/material/Tooltip";
import useAuth from "../../../hooks/useAuth";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import useOpenSignedPdfUrl from "../../../hooks/useOpenSignedPdfUrl";
import useRoles from "../../../hooks/user/useRoles";
import { DELETE_INTERNAL_WORK_ORDER_MUTATION } from "../../../constants/graphql/mutations";
import ShareModal from "../../../views/inventory/requests/share/ShareModal";
import InternalWorkOrderAttachmentsModal from "./InternalWorkOrderAttachmentsModal";
import InternalWorkOrderPickingModal from "../../../views/inventory/requests/orders/InternalWorkOrderPickingModal";
import RevisionLogModal from "../../RevisionLogModal";
import useQuery from "../../../hooks/useQuery";
import PdfReportMenuItem from "../../PdfReportMenuItem";

export default function InternalActions({ item, refetch }) {
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const { user } = useAuth();
  const { settings } = user.company;
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const [openRemove, setOpenRemove] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [openAttachments, setOpenAttachments] = useState(false);
  const [openRevisionLog, setOpenRevisionLog] = useState(false);
  const [deleteInternalWorkOrder] = useMutation(DELETE_INTERNAL_WORK_ORDER_MUTATION);
  const internalWorkOrderPicking = query.get("internalWorkOrderPicking");
  const iwoChargesSummaryId = query.get("iwoChargesSummaryId");
  const openPdfReport = useOpenSignedPdfUrl({
    resourceType: "internal_work_order_pdfs",
    resourceId: item.id,
  });
  const openRegeneratedPdfReport = useOpenSignedPdfUrl({
    resourceType: "internal_work_order_pdfs",
    resourceId: item.id,
    extraQueryParams: { force_pdf_regeneration: true },
  });

  const toggleRemove = () => setOpenRemove((v) => !v);
  const toggleShare = () => setOpenShare((v) => !v);
  const toggleAttachments = () => setOpenAttachments((v) => !v);
  const toggleRevisionLog = () => setOpenRevisionLog((v) => !v);

  const toggleEdit = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("internalWorkOrderId", item.id);
    navigate({ search: searchParams.toString() });
  };

  const handleDelete = useCallback(async () => {
    await deleteInternalWorkOrder({ variables: { id: item.id } });
    if (refetch) await refetch();
    setOpenRemove(false);
  }, [item?.id, deleteInternalWorkOrder, refetch]);

  const togglePickModal = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (internalWorkOrderPicking === item.id) {
      searchParams.delete("internalWorkOrderPicking");
      navigate({ search: searchParams.toString() });
    } else {
      searchParams.set("internalWorkOrderPicking", item.id);
      navigate({ search: searchParams.toString() });
    }
  }, [internalWorkOrderPicking, navigate, item.id]);

  const toggleChargesSummary = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    if (iwoChargesSummaryId === item.id) {
      searchParams.delete("iwoChargesSummaryId");
      searchParams.delete("openChargesInfoModal");
    } else {
      searchParams.set("iwoChargesSummaryId", item.id);
    }
    navigate({ search: searchParams.toString() });
  }, [iwoChargesSummaryId, item.id, location.search, navigate]);

  const popupState = usePopupState({ variant: "popover", popupId: `internal-actions-${item.id}` });

  return (
    <>
      {location.pathname.includes("/inventory") && settings.manual_location_checkout && (
        <Tooltip title="Internal Work Order Picking">
          <IconButton color="inherit" onClick={togglePickModal}>
            <ChecklistIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Edit Order">
        <IconButton size="small" onClick={toggleEdit}>
          <EditOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="More Options">
        <IconButton {...bindTrigger(popupState)} size="small">
          <MoreVertIcon color="action" />
        </IconButton>
      </Tooltip>
      <MuiMenu {...bindMenu(popupState)}>
        <PdfReportMenuItem
          onOpen={openPdfReport}
          onRegenerate={openRegeneratedPdfReport}
          onClose={popupState.close}
        />
        <MenuItem
          onClick={() => {
            toggleAttachments();
            popupState.close();
          }}
        >
          <ListItemIcon>
            <AttachFileIcon />
          </ListItemIcon>
          <ListItemText primary="Attachments" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggleShare();
            popupState.close();
          }}
        >
          <ListItemIcon>
            <IosShareIcon />
          </ListItemIcon>
          <ListItemText primary="Share Order" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggleRevisionLog();
            popupState.close();
          }}
        >
          <ListItemIcon>
            <HistoryIcon />
          </ListItemIcon>
          <ListItemText primary="Revision Log" />
        </MenuItem>
        {isAdminOrManager && (
          <MenuItem
            onClick={() => {
              toggleChargesSummary();
              popupState.close();
            }}
          >
            <ListItemIcon>
              <RequestQuoteIcon />
            </ListItemIcon>
            <ListItemText primary="IWO Charges Summary" />
          </MenuItem>
        )}
        {isAdminOrManager && (
          <MenuItem
            onClick={() => {
              toggleRemove();
              popupState.close();
            }}
          >
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText primary="Delete Order" />
          </MenuItem>
        )}
      </MuiMenu>
      {openRemove && (
        <ConfirmDeleteModal
          title="Delete Order"
          descr="Are you sure you want to delete this order?"
          onClose={toggleRemove}
          onDelete={handleDelete}
        />
      )}
      {openShare && (
        <ShareModal
          onClose={toggleShare}
          resourceType="internal_work_order_pdfs"
          resourceId={item.id}
        />
      )}
      {openAttachments && (
        <InternalWorkOrderAttachmentsModal id={item.id} onClose={toggleAttachments} />
      )}
      {openRevisionLog && (
        <RevisionLogModal
          itemId={item.id}
          itemType="InternalWorkOrder"
          onClose={toggleRevisionLog}
        />
      )}
      {internalWorkOrderPicking === item.id && (
        <InternalWorkOrderPickingModal id={item.id} onClose={togglePickModal} />
      )}
    </>
  );
}
